/**
 * HartTable - Renders data tables for 5 entity types
 * (jobs, analysts, vendors, clients, facilities).
 * Each type has unique columns and row rendering logic.
 */
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import TableContainerWithScrollShadow from "components/TableContainerWithScrollShadow";
import { canContribute, formatDateOnly, formatDateRange } from "../hartUtils";
import { HART_TABLES_CONFIG } from "../hartTablesConfig";

// Shared link styles for all table links
const linkStyles = {
  color: 'info.light',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline'
  }
};

// Formats a date string to localized format
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

// Get unique job numbers from sessions array (for vendors/facilities)
// Format: [{job_no, job_name, sessions: [...]}]
const getUniqueJobNos = (sessions) => {
  if (!sessions || !sessions.length) return [];
  return sessions.map(s => s.job_no).filter(Boolean);
};

// Flatten sessions from grouped format to flat array of session IDs
const flattenSessions = (sessions) => {
  if (!sessions || !sessions.length) return [];
  return sessions.flatMap(group => group.sessions || []);
};

// Get all unique vendors from a list of sessions
// Sessions come from the jobs endpoint and include vendor_name/vendor_id
const getSessionVendors = (sessions) => {
  if (!sessions || !sessions.length) return [];

  const vendorsById = new Map();

  sessions.forEach((session) => {
    const { vendor_id, vendor_name } = session || {};
    if (!vendor_id || !vendor_name) return;

    if (!vendorsById.has(vendor_id)) {
      vendorsById.set(vendor_id, {
        vendor_id,
        vendor_name,
      });
    }
  });

  return Array.from(vendorsById.values());
};

// Maximum number of items to display before showing "+X more" link
const MAX_ITEMS_DISPLAY = 15;


/**
 * Reusable component for rendering comma-separated linked lists
 * Supports truncation with "+X more" link when items exceed maxItems
 * 
 * @param {Array} items - Items to render
 * @param {Function} getLink - Function to get link URL for each item
 * @param {Function} getLabel - Function to get display label for each item
 * @param {Function} getItemKey - Function to get unique key for each item
 * @param {string} rowKey - Parent row key for unique keys
 * @param {string} itemKeyPrefix - Prefix for item keys
 * @param {number} maxItems - Maximum items to show before truncating (default: MAX_ITEMS_DISPLAY)
 * @param {string} moreLink - Link for the "+X more" text (optional)
 */
const LinkList = ({ items, getLink, getLabel, getItemKey, rowKey, itemKeyPrefix, maxItems = MAX_ITEMS_DISPLAY, moreLink }) => {
  if (!items || items.length === 0) return '-';
  
  const itemsToShow = items.slice(0, maxItems);
  const remainingCount = items.length - maxItems;
  
  return (
    <>
      {itemsToShow.map((item, index) => (
        <span key={`${rowKey}-${itemKeyPrefix}-${getItemKey ? getItemKey(item, index) : (typeof item === 'string' ? item : index)}`}>
          <Box component={Link} to={getLink(item)} sx={linkStyles}>
            {getLabel(item)}
          </Box>
          {index < itemsToShow.length - 1 ? ' | ' : ''}
        </span>
      ))}
      {remainingCount > 0 && (
        <span>
          {' | '}
          {moreLink ? (
            <Box component={Link} to={moreLink} sx={{ ...linkStyles, fontWeight: 600 }}>
              (+{remainingCount} more)
            </Box>
          ) : (
            <span style={{ fontWeight: 600 }}>(+{remainingCount} more)</span>
          )}
        </span>
      )}
    </>
  );
};

/**
 * HartTable - Displays tabular data for different Hart entity types
 *
 * @param {string} tableType - The type of table to display (jobs, analysts, vendors, clients, facilities)
 * @param {Array} data - The paginated data to display in the table
 * @param {string} projectId - The current project ID for building links
 * @param {Function} onDelete - Callback function when delete button is clicked
 */
const HartTable = ({ tableType, data, projectId, onDelete }) => {
  // Compute once per render, not per row
  const canDelete = canContribute(projectId);

  // Renders table rows based on the current table type
  const renderTableRow = (row, rowKey) => {

  switch (tableType) {
    case 'jobs':
      return (
        <TableRow key={rowKey}>
          <TableCell>
            <Box
              component={Link}
              to={`/projects/${projectId}/hart/jobs/${row.job_no}`}
              sx={linkStyles}
              state={{ job: row }}
            >
              {row.job_no}
            </Box>
          </TableCell>
          <TableCell>
            {row.job_name || '-'}
          </TableCell>
          <TableCell sx={{ minWidth: 120 }}>{formatDateRange(row.start_date, row.end_date)}</TableCell>
          <TableCell sx={{ minWidth: 100 }}>{row.created ? formatDateOnly(row.created) : '-'}</TableCell>
          <TableCell sx={{ minWidth: 100 }}>{row.close_date ? formatDateOnly(row.close_date) : '-'}</TableCell>
          <TableCell>
            {row.job_type && row.job_type.length > 0 ? (
              row.job_type.map((jobType, index) => (
                <span key={`${rowKey}-jobtype-${index}-${jobType}`}>
                  {jobType}
                  {index < row.job_type.length - 1 ? ' | ' : ''}
                </span>
              ))
            ) : '-'}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {row.has_oversample === true ? 'True' : row.has_oversample === false ? 'False' : '—'}
          </TableCell>
          <TableCell>
            {row.abstract && row.abstract.trim().length > 0 ? (
              row.abstract.slice(0, 50) + '...'
            ) : '-'}
          </TableCell>
          <TableCell>
            <LinkList
              items={row.analysts}
              getLink={(a) => `/projects/${projectId}/hart/analysts/${a.id}`}
              getLabel={(a) => a.name}
              getItemKey={(a, i) => a.id || i}
              rowKey={rowKey}
              itemKeyPrefix="analyst"
              moreLink={`/projects/${projectId}/hart/jobs/${row.job_no}`}
            />
          </TableCell>
          <TableCell>
            <LinkList
              items={row.sessions}
              getLink={(s) => `/projects/${projectId}/hart/sessions/${s.session_id}`}
              getLabel={(s) => s.session_id}
              getItemKey={(s, i) => s.session_id || i}
              rowKey={rowKey}
              itemKeyPrefix="session"
              moreLink={`/projects/${projectId}/hart/jobs/${row.job_no}`}
            />
          </TableCell>
          <TableCell>
            {row.session_types && row.session_types.length > 0 ? row.session_types.join(' | ') : '-'}
          </TableCell>
          <TableCell>
            <LinkList
              items={getSessionVendors(row.sessions)}
              getLink={(v) => `/projects/${projectId}/hart/vendors/${v.vendor_id}`}
              getLabel={(v) => v.vendor_name}
              getItemKey={(v, i) => v.vendor_id || i}
              rowKey={rowKey}
              itemKeyPrefix="vendor"
              moreLink={`/projects/${projectId}/hart/jobs/${row.job_no}`}
            />
          </TableCell>
          <TableCell>
            <LinkList
              items={row.hosts}
              getLink={(h) => `/projects/${projectId}/hart/vendors/${h.vendor_id}`}
              getLabel={(h) => h.name}
              getItemKey={(h, i) => h.vendor_id || i}
              rowKey={rowKey}
              itemKeyPrefix="host"
              moreLink={`/projects/${projectId}/hart/jobs/${row.job_no}`}
            />
          </TableCell>
          <TableCell>
            <LinkList
              items={row.recruiters}
              getLink={(r) => `/projects/${projectId}/hart/vendors/${r.vendor_id}`}
              getLabel={(r) => r.name}
              getItemKey={(r, i) => r.vendor_id || i}
              rowKey={rowKey}
              itemKeyPrefix="recruiter"
              moreLink={`/projects/${projectId}/hart/jobs/${row.job_no}`}
            />
          </TableCell>
          <TableCell>
            {row.job_category || '-'}
          </TableCell>
          <TableCell>
            {row.firewall_status || '-'}
          </TableCell>
          <TableCell>
            {row.release_status || '-'}
          </TableCell>
          <TableCell>
            {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Active'}
          </TableCell>
          {canDelete && (
            <TableCell align="right" sx={{ width: '80px' }}>
              <IconButton
                onClick={() => onDelete(row)}
                size="small"
                color="error"
                title="Delete Job"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
      );

    case 'analysts':
      return (
        <TableRow key={rowKey}>
          <TableCell>
            <Box
              component={Link}
              to={`/projects/${projectId}/hart/analysts/${row.id}`}
              state={{ analyst: row }}
              sx={linkStyles}
            >
              {row.name || '-'}
            </Box>
          </TableCell>
          <TableCell>
            {row.email || '-'}
          </TableCell>
          <TableCell>
            <LinkList
              items={row.jobs}
              getLink={(job) => `/projects/${projectId}/hart/jobs/${typeof job === 'object' ? job.job_no : job}`}
              getLabel={(job) => typeof job === 'object' ? job.job_no : job}
              getItemKey={(job, i) => (typeof job === 'object' ? job.job_no : job) || i}
              rowKey={rowKey}
              itemKeyPrefix="job"
              moreLink={`/projects/${projectId}/hart/analysts/${row.id}`}
            />
          </TableCell>
        </TableRow>
      );

    case 'vendors':
      return (
        <TableRow key={rowKey}>
          <TableCell>
            {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Active'}
          </TableCell>
          <TableCell>
            {row.type || '-'}
          </TableCell>
          <TableCell>
            <Box
              component={Link}
              to={`/projects/${projectId}/hart/vendors/${row.vendor_id}`}
              state={{ vendor: row }}
              sx={linkStyles}
            >
              {row.name || '-'}
            </Box>
          </TableCell>
          <TableCell>
            <LinkList
              items={getUniqueJobNos(row.sessions)}
              getLink={(jobNo) => `/projects/${projectId}/hart/jobs/${jobNo}`}
              getLabel={(jobNo) => jobNo}
              rowKey={rowKey}
              itemKeyPrefix="jobno"
              moreLink={`/projects/${projectId}/hart/vendors/${row.vendor_id}`}
            />
          </TableCell>
          <TableCell>
            <LinkList
              items={flattenSessions(row.sessions)}
              getLink={(sessionId) => `/projects/${projectId}/hart/sessions/${sessionId}`}
              getLabel={(sessionId) => sessionId}
              getItemKey={(sessionId, i) => sessionId || i}
              rowKey={rowKey}
              itemKeyPrefix="session"
              moreLink={`/projects/${projectId}/hart/vendors/${row.vendor_id}`}
            />
          </TableCell>
          <TableCell>
            {row.telephone || '-'}
          </TableCell>
          <TableCell>
            {row.fax || '-'}
          </TableCell>
          <TableCell>
            {row.email || '-'}
          </TableCell>
          <TableCell>
            {[row.address_1, row.address_2, row.address_3].filter(Boolean).join(' | ') || '-'}
          </TableCell>
          <TableCell>
            {row.city || '-'}
          </TableCell>
          <TableCell>
            {row.state || '-'}
          </TableCell>
          <TableCell>
            {row.zip_code || '-'}
          </TableCell>
          <TableCell>
            {row.notes && row.notes.trim().length > 0 ? (
              row.notes.length > 50 ? row.notes.slice(0, 50) + '...' : row.notes
            ) : '-'}
          </TableCell>
          {canDelete && (
            <TableCell align="right" sx={{ width: '80px' }}>
              <IconButton
                onClick={() => onDelete(row)}
                size="small"
                color="error"
                title="Delete Vendor"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
      );

    case 'clients':
      return (
        <TableRow key={rowKey}>
          <TableCell>
            {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Active'}
          </TableCell>
          <TableCell>
            {row.org_id || '-'}
          </TableCell>
          <TableCell>
            <Box
              component={Link}
              to={`/projects/${projectId}/hart/clients/${row.org_id}`}
              state={{ client: row }}
              sx={linkStyles}
            >
              {row.name || '-'}
            </Box>
          </TableCell>
          <TableCell>
            {row.org_address || '-'}
          </TableCell>
          <TableCell>
            {row.org_website ? (
              <Box
                component="a"
                href={row.org_website}
                target="_blank"
                rel="noopener noreferrer"
                sx={linkStyles}
              >
                {row.org_website}
              </Box>
            ) : '-'}
          </TableCell>
          <TableCell>
            {row.org_phone || '-'}
          </TableCell>
          <TableCell>
            {row.sector || '-'}
          </TableCell>
          <TableCell>
            {row.year_started || '-'}
          </TableCell>
          <TableCell>
            {row.parent_client || row.parent_organization || '-'}
          </TableCell>
          <TableCell>
            <LinkList
              items={row.jobs}
              getLink={(job) => `/projects/${projectId}/hart/jobs/${job.job_no}`}
              getLabel={(job) => job.job_no}
              getItemKey={(job, i) => job.job_no || i}
              rowKey={rowKey}
              itemKeyPrefix="job"
              moreLink={`/projects/${projectId}/hart/clients/${row.org_id}`}
            />
          </TableCell>
          {canDelete && (
            <TableCell align="right" sx={{ width: '80px' }}>
              <IconButton
                onClick={() => onDelete(row)}
                size="small"
                color="error"
                title="Delete Client"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
      );

    case 'facilities':
      return (
        <TableRow key={rowKey}>
          <TableCell>
            {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Active'}
          </TableCell>
          <TableCell>
            <Box
              component={Link}
              to={`/projects/${projectId}/hart/facilities/${row.facility_id}`}
              state={{ facility: row }}
              sx={linkStyles}
            >
              {row.name || '-'}
            </Box>
          </TableCell>
          <TableCell>
            <LinkList
              items={getUniqueJobNos(row.sessions)}
              getLink={(jobNo) => `/projects/${projectId}/hart/jobs/${jobNo}`}
              getLabel={(jobNo) => jobNo}
              rowKey={rowKey}
              itemKeyPrefix="jobno"
              moreLink={`/projects/${projectId}/hart/facilities/${row.facility_id}`}
            />
          </TableCell>
          <TableCell>
            <LinkList
              items={flattenSessions(row.sessions)}
              getLink={(sessionId) => `/projects/${projectId}/hart/sessions/${sessionId}`}
              getLabel={(sessionId) => sessionId}
              getItemKey={(sessionId, i) => sessionId || i}
              rowKey={rowKey}
              itemKeyPrefix="session"
              moreLink={`/projects/${projectId}/hart/facilities/${row.facility_id}`}
            />
          </TableCell>
          <TableCell>
            {row.telephone || '-'}
          </TableCell>
          <TableCell>
            {row.email || '-'}
          </TableCell>
          <TableCell>
            {[row.address_1, row.address_2, row.address_3].filter(Boolean).join(' | ') || '-'}
          </TableCell>
          <TableCell>
            {row.city || '-'}
          </TableCell>
          <TableCell>
            {row.state || '-'}
          </TableCell>
          <TableCell>
            {row.zip_code || '-'}
          </TableCell>
          {canDelete && (
            <TableCell align="right" sx={{ width: '80px' }}>
              <IconButton
                onClick={() => onDelete(row)}
                size="small"
                color="error"
                title="Delete Facility"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
      );

    default:
      return null;
  }
};

  return (
    <TableContainerWithScrollShadow component={Paper} sx={{ border: 0 }}>
      <Table sx={{ minWidth: getTableMinWidth(tableType) }}>
        <TableHead>
          <TableRow>
            {getTableHeaders(tableType, projectId)}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => {
            // Get the key based on table type - ensure it's always defined and unique
            let rowKey;
            switch (tableType) {
              case 'jobs':
                rowKey = row.job_no || `job-${index}`;
                break;
              case 'analysts':
                rowKey = row.id || `analyst-${index}`;
                break;
              case 'vendors':
                rowKey = row.vendor_id || `vendor-${index}`;
                break;
              case 'clients':
                rowKey = row.org_id || `client-${index}`;
                break;
              case 'facilities':
                rowKey = row.facility_id || `facility-${index}`;
                break;
              default:
                rowKey = row.id || row.name || `row-${index}`;
            }
            // Pass key as prop to renderTableRow
            return renderTableRow(row, rowKey);
          })}
        </TableBody>
      </Table>
    </TableContainerWithScrollShadow>
  );
};

// Returns table headers based on the current table type
const getTableHeaders = (tableType, projectId) => {
  const canDelete = canContribute(projectId);
  const tableConfig = HART_TABLES_CONFIG[tableType];

  if (!tableConfig) return null;

  return (
    <>
      {tableConfig.map((col, index) => (
        <TableCell key={index} sx={{ whiteSpace: 'nowrap' }}>
          {col.label}
        </TableCell>
      ))}
      {canDelete && (
        <TableCell align="right" sx={{ width: '60px' }}></TableCell>
      )}
    </>
  );
};

/**
 * Returns minimum table width based on table type
 * These widths ensure horizontal scrolling is enabled when content exceeds viewport
 */
const getTableMinWidth = (tableType) => {
  switch (tableType) {
    case 'jobs':
      return 1800;
    case 'analysts':
      return 1000;
    case 'vendors':
      return 2400; 
    case 'clients':
      return 2200; 
    case 'facilities':
      return 2200;
    default:
      return 1200;
  }
};

export default HartTable;
